import React from 'react';
import './LandingFooter.css';

export const NestedLandingFooterContext = React.createContext(false);

type FooterLink = { to: string; label: string };
type Props = {
  homepage?: boolean;
  relatedLinks?: FooterLink[];
  relatedHeading?: string;
};

export const footerGroups = [
  { title: 'Explore the product', links: [
    { to: '/pricing', label: 'Plans & pricing' },
    { to: '/traffic-bot', label: 'Traffic bot', homeLabel: 'Campaign automation' },
    { to: '/website-traffic-generator', label: 'Website traffic generator', homeLabel: 'Website session generator' },
    { to: '/mcp-server', label: 'AI assistants & MCP' },
    { to: '/white-label-traffic-api', label: 'Developer API' },
  ] },
  { title: 'Find your solution', links: [
    { to: '/buy-website-traffic', label: 'Buy website traffic', homeLabel: 'Website visitor campaigns' },
    { to: '/cheap-website-traffic', label: 'Affordable website traffic', homeLabel: 'Budget-friendly campaigns' },
    { to: '/buy-organic-traffic', label: 'Organic traffic campaigns', homeLabel: 'Search-referred sessions' },
    { to: '/free-traffic-bot', label: 'Free traffic bot test', homeLabel: 'Try a visitor campaign' },
  ] },
  { title: 'Compare your options', links: [
    { to: '/alternatives/sparktraffic', label: 'SparkTraffic alternative', homeLabel: 'Campaign platform comparison' },
    { to: '/alternatives/trafficpeak', label: 'TrafficPeak alternative', homeLabel: 'Visitor delivery comparison' },
    { to: '/alternatives/serpclix', label: 'SerpClix alternative' },
    { to: '/best-websites-to-buy-traffic', label: 'Website traffic provider comparison', homeLabel: 'Compare visitor providers' },
  ] },
  { title: 'Resources & support', links: [
    { to: '/helpdesk', label: 'Help center' },
    { to: '/blog', label: 'Guides & insights' },
    { to: '/about', label: 'About Traffic Creator' },
    { to: '/login', label: 'Log in to your workspace' },
  ] },
];

/** Ordinary anchors remain crawlable in SSR and work across the standalone builds. */
export default function LandingFooter({ homepage = false, relatedLinks, relatedHeading }: Props) {
  const nested = React.useContext(NestedLandingFooterContext);
  if (nested) return null;
  return <footer className="tc-landing-footer" lang="en" dir="ltr" aria-label="Website footer">
    <div className="tc-landing-footer__inner">
      <div className="tc-landing-footer__intro">
        <div><a className="tc-landing-footer__brand" href="/" aria-label="Traffic Creator home">TRAFFIC <span>CREATOR</span></a>
          <p>Configure your visitors. Control your delivery.<br />Keep every campaign in view.</p></div>
        <div className="tc-landing-footer__next"><strong>Make your next visit count.</strong><p>Explore the plans or build your first campaign.</p><div><a className="tc-landing-footer__cta" href="/signup">Create your account <span aria-hidden="true">↗</span></a><a href="/pricing">View pricing <span aria-hidden="true">→</span></a></div></div>
      </div>
      <nav className="tc-landing-footer__nav" aria-label="Explore products, solutions and resources">
        {footerGroups.map(group => <div key={group.title}><h2>{group.title}</h2><ul>{group.links.map(link => <li key={link.to}><a href={link.to}>{homepage && 'homeLabel' in link ? link.homeLabel : link.label}</a></li>)}</ul></div>)}
      </nav>
      {relatedLinks && relatedLinks.length > 0 && <nav className="tc-landing-footer__related" aria-label={relatedHeading || 'Related services'}><strong>{relatedHeading || 'Related services'}</strong><ul>{relatedLinks.slice(0,6).map(link => <li key={link.to}><a href={link.to} lang={/^\/[a-z]{2}\//.test(link.to) ? link.to.split('/')[1] : 'en'}>{link.label}</a></li>)}</ul></nav>}
      <div className="tc-landing-footer__bottom">
        <p>© {new Date().getFullYear()} {homepage ? 'Traffic Creator' : 'EasyTrafficBot UG (haftungsbeschränkt)'}<br /><span>Amtsgericht Wuppertal · HRB 30863</span></p>
        <nav aria-label="Legal information"><a href="/legal/privacy">Privacy policy</a><a href="/legal/terms">Terms of service</a><a href="/legal/refund">Refund policy</a><a href="/legal/impressum">Company details</a></nav>
      </div>
    </div>
  </footer>;
}
